import { demoNewsletterConfirmPath, demoOrderReturnNote, demoSavedFeatures, demoShopBackend } from '../demo/shopBackend'
import { call, hasBackend } from '../core/api/http'
import { endpoints } from '../core/api/endpoints'
import type { Catalog } from '../core/types/catalog'
import type { SiteSettings } from '../core/types/site'
import type { PageContent } from '../core/types/pages'
import type { AvailabilityDay } from '../core/types/agenda'
import type { AvailabilityFilter, OrderPreview, RequestOptions, ShopBackend } from '../core/types/backend'
import type {
  BookingRequest,
  BookingResponse,
  NewsletterConfirmRequest,
  NewsletterRequest,
  OkResponse,
  OrderRequest,
  OrderResponse,
  OrderSourceRequest,
  OrderStatusResponse,
  QuizLeadRequest,
  RetryPaymentResponse,
} from '../core/types/requests'
import type { VoucherCheckRequest, VoucherCheckResponse } from '../core/types/vouchers'

/**
 * Webshop ↔ backend. De echte kant staat hieronder (paden uit core/api/endpoints.ts);
 * zonder API_BASE (core/api/http.ts) neemt de demo-backend het over.
 *
 * Demo eruit halen: API_BASE invullen, de regel `const impl = …` hieronder (en in
 * accountApi.ts en adminApi.ts) vervangen door `const impl = http`, en de map demo/
 * verwijderen — zie demo/README.md.
 */

// Transport en foutafhandeling staan in core/api/http.ts; hier doorgegeven voor de schermen.
export { ApiError, hasBackend, isAbortError, newRequestId } from '../core/api/http'

const E = endpoints.shop

// Geen generics meer: het antwoordtype komt uit het eindpunt. Koppel je hier het verkeerde
// eindpunt (bijv. E.site aan fetchCatalog), dan faalt de compiler op de ShopBackend-interface.
const http: ShopBackend = {
  fetchCatalog: (opts) => call(E.catalog, [], undefined, opts),
  fetchSite: (opts) => call(E.site, [], undefined, opts),
  fetchPages: (opts) => call(E.pages, [], undefined, opts),
  // De server rekent het totaal zelf; `preview` gaat niet mee.
  submitOrder: (order) => call(E.order, [], order),
  fetchOrderStatus: (orderId, token, opts) => call(E.orderStatus, [orderId, token], undefined, opts),
  retryPayment: (orderId, token) => call(E.retryPayment, [orderId, token]),
  submitOrderSource: (orderId, token, req) => call(E.orderSource, [orderId, token], req),
  submitBooking: (booking) => call(E.booking, [], booking),
  fetchAvailability: (treatmentSlug, filter, opts) => call(E.availability, [treatmentSlug, filter?.ignore ?? '', filter?.staff ?? ''], undefined, opts),
  submitQuizLead: (lead) => call(E.quizLead, [], lead),
  subscribeNewsletter: (signup) => call(E.newsletter, [], signup),
  confirmNewsletter: (req) => call(E.newsletterConfirm, [], req),
  checkVoucher: (req) => call(E.voucherCheck, [], req),
}

const impl: ShopBackend = hasBackend ? http : demoShopBackend

/** Alleen in de demo: uitleg op de terugkeerpagina als een bestelling niet gevonden wordt. Met een backend `null`. */
export const demoOrderNote: string | null = hasBackend ? null : demoOrderReturnNote

// ——— Ophalen ———

/** De volledige catalogus: producten, protocollen, behandelingen, klachten, reviews en artikelen. */
export const fetchCatalog = async (opts?: RequestOptions): Promise<Catalog> => impl.fetchCatalog(opts)

/** Instellingen van de zaak. `null` = de lokale instellingen blijven gelden. */
export const fetchSite = async (opts?: RequestOptions): Promise<SiteSettings | null> => impl.fetchSite(opts)

/** Alle paginateksten. `null` = de lokale teksten blijven gelden. */
export const fetchPages = async (opts?: RequestOptions): Promise<PageContent | null> => impl.fetchPages(opts)

// ——— Versturen ———

/** Bestelling plaatsen. `preview` gebruikt alleen de demo; de server rekent zelf. */
export const submitOrder = async (order: OrderRequest, preview: OrderPreview): Promise<OrderResponse> =>
  impl.submitOrder(order, preview)

/** Terugkeerpagina na Mollie: de betaalstatus volgens de server (webhook), nooit volgens de browser. */
export const fetchOrderStatus = async (orderId: string, token: string, opts?: RequestOptions): Promise<OrderStatusResponse> =>
  impl.fetchOrderStatus(orderId, token, opts)

/** Opnieuw betalen na mislukt of verlopen: dezelfde order, een nieuwe betaallink. */
export const retryPayment = async (orderId: string, token: string): Promise<RetryPaymentResponse> => impl.retryPayment(orderId, token)

/** Het antwoord op "Hoe kwam je bij ons?" op de bedankpagina. Vrijwillig; een fout hoeft de klant niet te zien. */
export const submitOrderSource = async (orderId: string, token: string, req: OrderSourceRequest): Promise<OkResponse> =>
  impl.submitOrderSource(orderId, token, req)

/** Afspraak aanvragen zonder in te loggen. Hoort het e-mailadres bij een account, dan koppelt de backend het. */
export const submitBooking = async (booking: BookingRequest): Promise<BookingResponse> => impl.submitBooking(booking)

/** Vrije tijden per salondag voor één behandeling, met wie het doet (rekening houdend met de agenda van de salon). */
export const fetchAvailability = async (treatmentSlug: string, filter?: AvailabilityFilter, opts?: RequestOptions): Promise<AvailabilityDay[]> =>
  impl.fetchAvailability(treatmentSlug, filter, opts)

export const submitQuizLead = async (lead: QuizLeadRequest): Promise<OkResponse> => impl.submitQuizLead(lead)

export const subscribeNewsletter = async (signup: NewsletterRequest): Promise<OkResponse> =>
  impl.subscribeNewsletter(signup)

/** De klik uit de bevestigingsmail: pas dan staat ze op de lijst. */
export const confirmNewsletter = async (req: NewsletterConfirmRequest): Promise<OkResponse> => impl.confirmNewsletter(req)

/** Cadeaubon bij afrekenen: saldo en waarvoor hij geldt, of 422 `voucher_*`. Boekt niets af; dat doet POST /orders. */
export const checkVoucher = async (req: VoucherCheckRequest): Promise<VoucherCheckResponse> => impl.checkVoucher(req)

/** Alleen in de demo: de onderdelen die het beheer opsloeg (anders via GET /site). Met een backend `null`. */
export const demoFeatures: (() => unknown) | null = hasBackend ? null : demoSavedFeatures

/** Alleen in de demo: de bevestigingslink die anders in de mail staat. Met een backend `null`. */
export const demoNewsletterLink: string | null = hasBackend ? null : demoNewsletterConfirmPath
